import { fetchUserLocationFromWebView, openSettingPage } from "@hooks/useWebviewCallback";
import { inApp } from "@utils/platformDetection.client";

const APP_LOCATION_PERMISSION_DENIED_MESSAGE =
  "Location access denied. Please enable location permissions for the app and try again.";
const BROWSER_LOCATION_PERMISSION_DENIED_MESSAGE =
  "Location access denied. Please enable location permissions in your browser site settings and try again.";

const getNavigatorGeolocationErrorMessage = (
  err: GeolocationPositionError
): string => {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return BROWSER_LOCATION_PERMISSION_DENIED_MESSAGE;
    case err.POSITION_UNAVAILABLE:
      return "Location information is unavailable. Please check your GPS/network connection.";
    case err.TIMEOUT:
      return "Location request timed out. Please try again.";
    default:
      return "An unknown error occurred while getting your location.";
  }
};

const getBrowserPosition = async (): Promise<{ lat: number; lon: number }> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported on this device"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
        });
      },
      (err) => {
        reject(new Error(getNavigatorGeolocationErrorMessage(err)));
      },
      {
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 300000,
      }
    );
  });
};

export async function getCurrentPosition(): Promise<{
  lat: number;
  lon: number;
}> {
  if (inApp()) {
    try {
      const appLocation = await fetchUserLocationFromWebView();

      if (
        appLocation?.status === "success" &&
        typeof appLocation.lat === "number" &&
        typeof appLocation.long === "number"
      ) {
        return {
          lat: appLocation.lat,
          lon: appLocation.long,
        };
      }

      if (appLocation?.status && appLocation.status !== "success") {
        throw new Error(APP_LOCATION_PERMISSION_DENIED_MESSAGE);
      }
    } catch (error) {
      setTimeout(() => {
        openSettingPage();
      }, 3000);
      if (!navigator.geolocation) {
        throw new Error(APP_LOCATION_PERMISSION_DENIED_MESSAGE);
      }
    }
  }

  return getBrowserPosition();
}
